import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink } from "../../../../common/components/ExternalLink";
import { SurveyPatchAnswerMutation } from "../../../../common/hooks/api/useSurvey";
import { Api } from "../../../../common/service/Api";
import { UploadVendorAgreementResponse } from "../../../../common/service/server/controller/StorageController";
import { PatchOrganizationSurveyAnswerRequest } from "../../../../common/service/server/dto/org/OrganizationSurveyRequestDto";
import {
  GDPRProcessorRecommendation,
  OrganizationDataRecipientDto,
  OrganizationVendorSettingsDto,
  ProcessingRegion as ProcessingRegionEnum,
  ProcessingRegion,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { VendorDto } from "../../../../common/service/server/dto/VendorDto";
import { UpdateVendorArgs } from "../../../pages/data-map/components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import {
  getSelectedUploadingVendors,
  getVendorManagedAdNetworksIds,
} from "../../../pages/get-compliant/data-recipients/shared";
import { vendorName } from "../../../util/resources/vendorUtils";
import {
  getVendorSettingsObject,
  isAdNetwork,
  isGoogleAnalytics,
  isSharingVendor,
  isShopify,
  isShopifyAudiences,
  isStorageLocation,
} from "../../../util/vendors";
import { VendorRadioOption } from "../../get-compliant/vendors/VendorFlagWithDocumentation";
import { Para } from "../../typography/Para";
import { Paras } from "../../typography/Paras";
import { VendorBooleanQuestion } from "../../vendor/VendorBooleanQuestion";
import { ProcessingRegionsSetting } from "./DataRecipientProcessingRegionsSetting";
import clsx from "clsx";
import { isUrl } from "../../../util/validation";
import { CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG } from "../../../surveys/sharingSurvey";
import { parseCheckboxSurveyAnswer } from "../../../util/surveyAnswerParsing";
import { useManagedAdNetworks } from "../../../../common/hooks/dataRecipientSettingsHooks";
import { OrganizationManagedAdNetworkDto } from "../../../../common/service/server/dto/OrganizationManagedAdNetworkDto";

export const calculateDisclosureMeta = (
  vendor: OrganizationDataRecipientDto | VendorDto | null,
  state: DisclosureState,
  custom: boolean,
  answers: Record<string, string> | null,
  allVendors: OrganizationDataRecipientDto[],
  customCategory?: string,
) => {
  let orgVendor: OrganizationDataRecipientDto | null = null;
  if (vendor && isOrganizationDataRecipient(vendor)) {
    orgVendor = vendor;
  }

  customCategory = customCategory || vendor?.category;
  const isAd = custom ? customCategory == "Ad Network" : vendor ? isAdNetwork(vendor) : false;
  const isSharing = custom
    ? isSharingVendor({ category: customCategory } as VendorDto)
    : vendor
    ? isSharingVendor(vendor)
    : false;

  vendor?.vendorKey === "shopify";
  const classifiedAsContractor = state.classification === "contractor";
  const classifiedAsThirdParty = state.classification === "third-party";
  const isConsumer = orgVendor?.collectionContext?.includes("CONSUMER") ?? true;
  const showIntInteraction =
    isConsumer &&
    !isStorageLocation(vendor) &&
    (custom
      ? !classifiedAsContractor && !isAd
      : !classifiedAsContractor &&
        !isAd &&
        !state.isShopifyAudiencesVendor &&
        classifiedAsThirdParty);

  const showSelling =
    isConsumer &&
    !isStorageLocation(vendor) &&
    (custom
      ? !classifiedAsContractor && !isAd
      : state.isShopifyAudiencesVendor ||
        (!classifiedAsContractor &&
          !isAd &&
          (classifiedAsThirdParty || vendor?.serviceProviderRecommendation != "SERVICE_PROVIDER")));

  const showSharing =
    isConsumer &&
    !isStorageLocation(vendor) &&
    (custom ? isSharing : !classifiedAsContractor && isSharing);
  const autoSelectThirdParty = Boolean(
    state.ccpaIsSelling ||
      state.ccpaIsSharing ||
      state.usesCustomAudience ||
      state.isAlwaysDataSelling,
  );

  // Parse the checkbox array for custom audience vendor feature
  const customAudienceManagementVendorIds = parseCheckboxSurveyAnswer(
    answers?.[CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG],
  );

  // Get potential managers for this vendor (ad networks that could manage custom audiences)
  // Allow vendors to manage their own custom audiences by not excluding themselves
  const potentialManagers =
    allVendors?.filter((v) => customAudienceManagementVendorIds.includes(v.id)) || [];

  const isCurrentVendorManagingCustomAudiences = customAudienceManagementVendorIds.includes(
    vendor?.id || "",
  );

  const questionMeta: DisclosureMeta = {
    showClassification: isConsumer && !isStorageLocation(vendor),
    showCookies: true,
    showIntInteraction: Boolean(showIntInteraction),
    showCustomAudience: isSharing && state.ccpaIsSharing,
    showSharing,
    showSelling,
    showGdprProcessorClassification: !isStorageLocation(vendor),

    lockCcpaIsSelling:
      state.isShopifyAudiencesVendor || (vendor ? isGoogleAnalytics(vendor) : false),
    autoSelectThirdPartyClassification: autoSelectThirdParty,
    lockClassification: Boolean(state.isShopifyAudiencesVendor || autoSelectThirdParty),
    lockGdprProcessorClassification: Boolean(state.isShopifyAudiencesVendor),
    // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
    // showFacebookLdu: vendor ? isFacebookAds(vendor) : false,
    // showGoogleRdp: vendor ? isGoogleAds(vendor) : false,
    showGoogleAnalytics: vendor ? isGoogleAnalytics(vendor) : false,
    showShopifySNI: vendor ? isShopify(vendor) : false,

    // This is only for the US Data Disclosure Classification
    showServiceProviderDocumentationInput: vendor ? !vendor.serviceProviderLanguageUrl : false,

    recommendClassificationProcessor: orgVendor?.gdprProcessorRecommendation === "Processor",
    recommendClassificationController: orgVendor?.gdprProcessorRecommendation === "Controller",
    lockSccDocumentation: Boolean(orgVendor?.gdprSccDocumentationUrl),

    potentialCustomAudienceManagers: potentialManagers,
    isCurrentVendorManagingCustomAudiences,
  };

  return questionMeta;
};

export type DisclosureMeta = {
  showClassification: boolean;
  showCookies: boolean;
  showIntInteraction: boolean;
  showCustomAudience: boolean;
  showSharing: boolean;
  showSelling: boolean;

  // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
  // showFacebookLdu: boolean;
  // showGoogleRdp: boolean;
  showGoogleAnalytics: boolean;
  showShopifySNI: boolean;

  showServiceProviderDocumentationInput: boolean;

  lockCcpaIsSelling?: boolean;
  autoSelectThirdPartyClassification: boolean;
  lockClassification: boolean;
  showGdprProcessorClassification: boolean;
  lockGdprProcessorClassification: boolean;

  recommendClassificationProcessor: boolean;
  recommendClassificationController: boolean;

  lockSccDocumentation: boolean;

  potentialCustomAudienceManagers: OrganizationDataRecipientDto[];
  isCurrentVendorManagingCustomAudiences: boolean;
};

interface VendorClassificationEditProps {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto | VendorDto;
  isThirdParty: boolean;
  disclosureState: DisclosureState;
  disclosureMeta: DisclosureMeta;
  updateDisclosureState: (update: Partial<DisclosureState>) => void;
}

export type ClassificationType = "service-provider" | "third-party" | "contractor" | null;

export type DisclosureState = {
  _isStateInitialized: boolean; // Hack to distinguish between a "null" state and the actual initial state from a data recipient

  classification: ClassificationType;

  publicTosUrl: string;

  serviceProviderFile: File | null;
  serviceProviderFilename: string | null;

  ccpaIsSelling: boolean | null;
  ccpaIsSharing: boolean | null;
  isUploadVendor: boolean | null;
  isAlwaysUploadVendor: boolean | null;
  usesCustomAudience: boolean | null;
  usesVendorManagedCustomAudience: boolean | null;
  managedByVendorIds: string[] | null;
  usesShopifyAudience: boolean | null;
  isShopifyAudiencesVendor: boolean | null;
  isAlwaysDataSelling: boolean | null;

  // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
  // facebookLdu: boolean | null;
  // googleRdp: boolean | null;
  googleAnalytics: boolean | null;
  shopifySNI: boolean | null;
  processorClassification: GDPRProcessorRecommendation | null;
  processorGuaranteeUrl: string | null;
  processorGuaranteeFile: File | null;
  processorGuaranteeFilename: string | null;
  processingRegions: ProcessingRegionEnum[];

  gdprHasSccs: boolean | null;
  gdprHasSccSetting: boolean | null;
  gdprSccUrl: string | null;
  gdprSccFile: File | null;
  gdprSccFilename: string | null;

  contractStoredInternally: boolean | null;
  gdprContractStoredInternally: boolean | null;
};

const isOrganizationDataRecipient = (
  vendor: OrganizationDataRecipientDto | VendorDto,
): vendor is OrganizationDataRecipientDto => {
  return (vendor as OrganizationDataRecipientDto).classificationOptions !== undefined;
};

export const initialStateFrom = (
  vendor: OrganizationDataRecipientDto | VendorDto,
  answers: Record<string, string> | null,
  org: OrganizationDto,
): DisclosureState => {
  if (!vendor) {
    return {
      _isStateInitialized: false,
      publicTosUrl: "",
      classification: null,
      serviceProviderFile: null,
      serviceProviderFilename: null,
      usesCustomAudience: null,
      usesVendorManagedCustomAudience: null,
      managedByVendorIds: null,
      usesShopifyAudience: null,
      isShopifyAudiencesVendor: null,
      isAlwaysDataSelling: null,
      ccpaIsSelling: null,
      ccpaIsSharing: null,
      isUploadVendor: null,
      isAlwaysUploadVendor: null,
      // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
      // facebookLdu: null,
      // googleRdp: null,
      googleAnalytics: null,
      shopifySNI: null,
      processorClassification: null,
      processorGuaranteeUrl: null,
      processorGuaranteeFile: null,
      processorGuaranteeFilename: null,
      processingRegions: [
        "UNITED_STATES",
        org.featureGdpr ? "EEA_UK" : null,
        org.featurePipeda ? "CANADA" : null,
      ].filter(Boolean) as ProcessingRegion[],
      gdprHasSccs: null,
      gdprHasSccSetting: null,
      gdprSccUrl: null,
      gdprSccFile: null,
      gdprSccFilename: null,
      contractStoredInternally: null,
      gdprContractStoredInternally: null,
    };
  }

  let orgVendor: OrganizationDataRecipientDto | null = null;
  if (vendor && isOrganizationDataRecipient(vendor)) {
    orgVendor = vendor;
  }

  const serviceProviderClassification = orgVendor?.classificationOptions.find(
    (x) => x.slug === "service-provider",
  );
  const thirdPartyClassification = orgVendor?.classificationOptions.find(
    (x) => x.slug === "third-party",
  );
  const contractorClassification = orgVendor?.classificationOptions.find(
    (x) => x.slug === "contractor",
  );

  let classification: ClassificationType = null;
  if (orgVendor) {
    if (orgVendor?.classification?.id === serviceProviderClassification?.id) {
      classification = "service-provider" as const;
    } else if (orgVendor?.classification?.id === thirdPartyClassification?.id) {
      classification = "third-party" as const;
    } else if (orgVendor?.classification?.id === contractorClassification?.id) {
      classification = "contractor" as const;
    }
  }

  const processorClassification = orgVendor?.gdprProcessorSetting;
  const isProcessor = processorClassification === "Processor";

  const processorGuaranteeUrl = isProcessor
    ? orgVendor?.gdprProcessorGuaranteeUrl
    : orgVendor?.gdprControllerGuaranteeUrl;

  const processorGuaranteeFilename = isProcessor
    ? orgVendor?.gdprProcessorGuaranteeFileName
    : orgVendor?.gdprControllerGuaranteeFileName;

  const processingRegions = orgVendor?.processingRegions;

  const uploadingVendors = answers ? getSelectedUploadingVendors(answers) : [];
  const isUploadVendor = orgVendor ? uploadingVendors.some((id) => id == vendor.id) : null;
  const isAlwaysDataSelling = orgVendor.isAlwaysDataSellingVendor;
  if (isAlwaysDataSelling) {
    // always-selling vendors are always considered third-party
    classification = "third-party" as const;
  }

  // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
  // const isFacebookLdu = answers ? answers["facebook-ldu"] == "true" : null;
  // const isGoogleRdp = answers ? answers["google-rdp"] == "true" : null;
  const isGA = vendor ? isGoogleAnalytics(vendor) : false;
  const isGoogleAnalyticsDataSharing = answers
    ? answers["google-analytics-data-sharing"] == "true"
    : null;
  const gaIsSellingState = isGA ? !isGoogleAnalyticsDataSharing : undefined;
  const isShopifySNIDataSharing = answers ? answers["shopify-sni"] == "true" : null;
  const isShopifyAudiencesVendor = vendor ? isShopifyAudiences(vendor) : false;
  const usesShopifyAudience = Boolean(
    (vendor as OrganizationDataRecipientDto)?.usesShopifyAudience,
  );

  const vendorManagedAdNetworksIds = getVendorManagedAdNetworksIds(answers);

  return {
    _isStateInitialized: true,
    publicTosUrl: orgVendor?.publicTosUrl,
    classification,
    ccpaIsSelling: isAlwaysDataSelling || (gaIsSellingState ?? orgVendor?.ccpaIsSelling),
    usesCustomAudience: orgVendor?.usesCustomAudience,
    usesVendorManagedCustomAudience: vendorManagedAdNetworksIds.includes(vendor.id),
    managedByVendorIds: [], // TODO: Load from API
    usesShopifyAudience,
    isShopifyAudiencesVendor,
    isAlwaysDataSelling,
    isUploadVendor,
    isAlwaysUploadVendor: orgVendor?.isAlwaysUploadVendor,
    ccpaIsSharing: orgVendor?.ccpaIsSharing,
    serviceProviderFile: null,
    serviceProviderFilename: orgVendor?.tosFileName,
    // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
    // facebookLdu: isFacebookLdu,
    // googleRdp: isGoogleRdp,
    googleAnalytics: isGoogleAnalyticsDataSharing,
    shopifySNI: isShopifySNIDataSharing,
    processorClassification,
    processorGuaranteeUrl: processorGuaranteeUrl
      ? processorGuaranteeUrl
      : orgVendor.gdprProcessorLanguageUrl,
    processorGuaranteeFile: null,
    processorGuaranteeFilename,
    processingRegions,
    gdprHasSccs: orgVendor?.gdprHasSccs,
    gdprHasSccSetting: orgVendor?.gdprHasSccSetting,
    gdprSccUrl: orgVendor?.gdprSccUrl,
    gdprSccFile: null,
    gdprSccFilename: orgVendor?.gdprSccFileName,
    contractStoredInternally: orgVendor?.contractStoredInternally,
    gdprContractStoredInternally: orgVendor?.gdprContractStoredInternally,
  };
};

export const isThirdPartyDocumentationOptional = (state: DisclosureState): boolean => {
  return state.classification == "third-party" && !state.ccpaIsSelling && !state.ccpaIsSharing;
};

export const isGDPRControllerDocumentationOptional = (state: DisclosureState): boolean => {
  return (
    state.processorClassification == "Controller" && !state.ccpaIsSelling && !state.ccpaIsSharing
  );
};

export const finishedDisclosure = (
  state: DisclosureState,
  meta: DisclosureMeta,
  org: OrganizationDto,
  requireDocumentation: boolean = true,
): boolean => {
  if (!meta) {
    return false;
  }

  const isUsRegion = state.processingRegions.some((r) => r === "UNITED_STATES");
  const isEeeUkRegion = state.processingRegions.some((r) => r === "EEA_UK");
  const isPIPEDARegion = state.processingRegions.some((r) => r === "CANADA");
  const isOrgGdprFeature = org.featureGdpr;

  const hasRegionSelected = isUsRegion || isEeeUkRegion || isPIPEDARegion;
  const hasClassification = !isUsRegion || state.classification != null || !meta.showClassification;
  const hasProcessorClassification =
    !meta.showGdprProcessorClassification ||
    !isOrgGdprFeature ||
    !isEeeUkRegion ||
    state.processorClassification != null;
  const hasPublicTosUrl = state.publicTosUrl && isUrl(state.publicTosUrl);
  const hasServiceProviderDocumentation =
    state.classification != "service-provider" ||
    !meta.showServiceProviderDocumentationInput ||
    state.contractStoredInternally === true ||
    state.serviceProviderFile ||
    hasPublicTosUrl ||
    !isUsRegion;
  const hasThirdPartyDocumentation =
    state.classification != "third-party" ||
    !meta.showServiceProviderDocumentationInput ||
    isThirdPartyDocumentationOptional(state) ||
    state.contractStoredInternally === true ||
    state.serviceProviderFile ||
    hasPublicTosUrl ||
    !isUsRegion;
  const hasContractorDocumentation =
    state.classification != "contractor" ||
    !meta.showServiceProviderDocumentationInput ||
    state.contractStoredInternally === true ||
    state.serviceProviderFile ||
    hasPublicTosUrl ||
    !isUsRegion;

  const hasProcessorDocumentation =
    !meta.showGdprProcessorClassification ||
    !isOrgGdprFeature ||
    !isEeeUkRegion ||
    isGDPRControllerDocumentationOptional(state) ||
    state.gdprContractStoredInternally === true ||
    (hasProcessorClassification &&
      (isUrl(state?.processorGuaranteeUrl ?? "") ||
        state.processorGuaranteeFilename ||
        state.processorGuaranteeFile));
  const hasSelling =
    !isUsRegion || !meta.showSelling || !!meta.lockCcpaIsSelling || state.ccpaIsSelling != null;
  const hasSharing = !isUsRegion || !meta.showSharing || state.ccpaIsSharing != null;
  const hasCustomAudience =
    !isUsRegion || !meta.showCustomAudience || state.usesCustomAudience != null;
  const isSelling = !isUsRegion || Boolean(state.ccpaIsSelling);
  const hasDirect =
    !isUsRegion ||
    !meta.showCookies ||
    !isSelling ||
    state.isUploadVendor != null ||
    state.isAlwaysUploadVendor != null;

  // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
  // const hasFacebookLdu = !meta.showFacebookLdu || state.facebookLdu != null;
  // const hasGoogleRdp = !meta.showGoogleRdp || state.googleRdp != null;
  const hasGoogleAnalytics = !meta.showGoogleAnalytics || state.googleAnalytics != null;
  const hasShopifySNI = !meta.showShopifySNI || state.shopifySNI != null;

  const documentationComplete =
    hasClassification &&
    hasProcessorClassification &&
    hasServiceProviderDocumentation &&
    hasThirdPartyDocumentation &&
    hasContractorDocumentation &&
    hasProcessorDocumentation;

  const finalResult =
    hasRegionSelected &&
    hasCustomAudience &&
    // Optionally allow the user to finish a vendor's disclosure settings without having
    // completed its documentation. This will result in the vendor showing as Incomplete
    // in the Data Map.
    (!requireDocumentation || documentationComplete) &&
    hasSelling &&
    hasSharing &&
    hasDirect &&
    // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
    // hasFacebookLdu &&
    // hasGoogleRdp &&
    hasGoogleAnalytics &&
    hasShopifySNI;

  return finalResult;
};

export const vendorSettingsFromDisclosureState = (
  vendor: OrganizationDataRecipientDto,
  state: DisclosureState,
): OrganizationVendorSettingsDto => {
  const current = getVendorSettingsObject(vendor);

  const updated = {
    ...current,
    ccpaIsSelling: state.ccpaIsSelling,
    ccpaIsSharing: state.ccpaIsSharing,
    usesCustomAudience: state.usesCustomAudience,
    usesShopifyAudience: state.usesShopifyAudience,
    publicTosUrl: state.publicTosUrl,
    contractStoredInternally: state.contractStoredInternally,
    gdprContractStoredInternally: state.gdprContractStoredInternally,
    gdprProcessorSetting: state.processorClassification,
    gdprHasSccSetting: state.gdprHasSccSetting,
    gdprSccUrl: state.gdprSccUrl,

    classificationId: vendor.classificationOptions?.find((opt) => opt.slug == state.classification)
      ?.id,
  };

  if (state.processorClassification === "Processor") {
    updated.gdprProcessorGuaranteeUrl = state.processorGuaranteeUrl;
    updated.gdprControllerGuaranteeUrl = null;
  } else if (state.processorClassification === "Controller") {
    updated.gdprProcessorGuaranteeUrl = null;
    updated.gdprControllerGuaranteeUrl = state.processorGuaranteeUrl;
  }

  return updated;
};

export const saveDisclosureUpdates = async (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  answers: Record<string, string>,
  state: DisclosureState,
  updateVendor: (v: UpdateVendorArgs) => Promise<void>,
  updateSurveyQuestions: SurveyPatchAnswerMutation,
) => {
  // 1 - add file if it exists, & is new
  let agreementResponse: UploadVendorAgreementResponse | null = null;
  if (state.serviceProviderFile && state.serviceProviderFilename != vendor.tosFileName) {
    // do the file upload
    agreementResponse = await Api.storage.postOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      state.serviceProviderFile.name,
      state.serviceProviderFile,
    );
  }

  const isUsRegion = state.processingRegions.find((r) => r == "UNITED_STATES") !== undefined;
  const isEeaUkRegion = state.processingRegions.find((r) => r == "EEA_UK") !== undefined;
  const isCanadaRegion = state.processingRegions.find((r) => r == "CANADA") !== undefined;

  let processorAgreementResponse: UploadVendorAgreementResponse | null = null;
  const isNewProcessorFile =
    state.processorClassification === "Processor" &&
    state.processorGuaranteeFilename != vendor.gdprProcessorGuaranteeFileName;
  const isNewControllerFile =
    state.processorClassification === "Controller" &&
    state.processorGuaranteeFilename != vendor.gdprControllerGuaranteeFileName;
  if (state.processorGuaranteeFile && (isNewProcessorFile || isNewControllerFile)) {
    processorAgreementResponse = await Api.storage.postOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      state.processorGuaranteeFile.name,
      state.processorGuaranteeFile,
    );
  }

  let sccDocumentationResponse: UploadVendorAgreementResponse | null = null;
  if (state.gdprSccFile && state.gdprSccFilename != vendor.gdprSccFileName) {
    sccDocumentationResponse = await Api.storage.postOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      state.gdprSccFile.name,
      state.gdprSccFile,
    );
  }

  // 2 - delete file if it doesn't exist & used to
  let deletedUpload = false;
  if (!isUsRegion || (state.serviceProviderFilename === "DELETE_" && vendor.tosFileName)) {
    // do the file delete
    await Api.storage.deleteOrganizationVendorAgreement(org.id, vendor.vendorId, vendor.tosFileKey);
    deletedUpload = true;
  }

  let deletedProcessorUpload = false;
  let deletedControllerUpload = false;
  const hasProcessorFile =
    state.processorClassification === "Processor" && Boolean(vendor.gdprProcessorGuaranteeFileKey);
  const hasControllerFile =
    state.processorClassification === "Controller" &&
    Boolean(vendor.gdprControllerGuaranteeFileKey);
  if (state.processorGuaranteeFilename === "DELETE_" && hasProcessorFile) {
    await Api.storage.deleteOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      vendor.gdprProcessorGuaranteeFileKey,
    );
    deletedProcessorUpload = true;
  }

  if (state.processorGuaranteeFilename === "DELETE_" && hasControllerFile) {
    await Api.storage.deleteOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      vendor.gdprControllerGuaranteeFileKey,
    );
    deletedControllerUpload = true;
  }

  let deletedSccDocumentation = false;
  if (state.gdprSccFilename === "DELETE_" && vendor.gdprSccFileName) {
    await Api.storage.deleteOrganizationVendorAgreement(
      org.id,
      vendor.vendorId,
      vendor.gdprSccFileKey,
    );
    deletedSccDocumentation = true;
  }

  // 3 - calculate new settings & save
  const settings: OrganizationVendorSettingsDto = vendorSettingsFromDisclosureState(vendor, state);

  if (agreementResponse) {
    settings.tosFileName = agreementResponse.fileName;
    settings.tosFileKey = agreementResponse.fileKey;
    settings.vendorContractReviewedId = null;
  } else {
    settings.tosFileName = vendor.tosFileName;
    settings.tosFileKey = vendor.tosFileKey;
  }

  if (deletedUpload || !isUsRegion) {
    settings.tosFileName = "DELETE_";
    settings.tosFileKey = null;
  }

  if (processorAgreementResponse || !isEeaUkRegion) {
    if (state.processorClassification === "Processor") {
      settings.gdprProcessorGuaranteeFileName = processorAgreementResponse?.fileName ?? null;
      settings.gdprProcessorGuaranteeFileKey = processorAgreementResponse?.fileKey ?? null;
    } else if (state.processorClassification === "Controller") {
      settings.gdprControllerGuaranteeFileName = processorAgreementResponse?.fileName ?? null;
      settings.gdprControllerGuaranteeFileKey = processorAgreementResponse?.fileKey ?? null;
    }
  }

  if (deletedProcessorUpload || !isEeaUkRegion) {
    settings.gdprProcessorGuaranteeFileName = "DELETE_";
    settings.gdprProcessorGuaranteeFileKey = null;
  }

  if (deletedControllerUpload || !isEeaUkRegion) {
    settings.gdprControllerGuaranteeFileName = "DELETE_";
    settings.gdprControllerGuaranteeFileKey = null;
  }

  if (sccDocumentationResponse || !isEeaUkRegion) {
    settings.gdprSccFileName = sccDocumentationResponse?.fileName ?? null;
    settings.gdprSccFileKey = sccDocumentationResponse?.fileKey ?? null;
  }

  if (deletedSccDocumentation || !isEeaUkRegion) {
    settings.gdprSccFileName = "DELETE_";
    settings.gdprSccFileKey = null;
  }

  if (state.processingRegions.length > 0) {
    settings.processingRegions = state.processingRegions;
  }

  await updateVendor({
    vendorId: vendor.vendorId,
    vendorSettings: settings,
  });

  // 4 - calculate new survey answers & save
  const directSellingVendors = getSelectedUploadingVendors(answers);

  const questions: PatchOrganizationSurveyAnswerRequest[] = [];

  updateVendorToSurveyResponse(
    directSellingVendors,
    vendor,
    state.isUploadVendor && (isUsRegion || isCanadaRegion),
    "vendor-upload-data-selection",
    questions,
  );

  // NOTE: Managed ad networks are now handled directly via the database API
  // when users interact with the checkboxes, so we don't need to save them here anymore
  // updateVendorToSurveyResponse(
  //   vendorManagedAdNetworksIds,
  //   vendor,
  //   state.usesCustomAudience && state.usesVendorManagedCustomAudience,
  //   CUSTOM_AUDIENCE_VENDOR_MANAGED_AD_NETWORKS_FEATURE_SLUG,
  //   questions,
  // );

  // POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
  // if (vendor && isFacebookAds(vendor)) {
  //   updateBooleanResponse("facebook-ldu", state.facebookLdu, questions);
  // }

  // if (vendor && isGoogleAds(vendor)) {
  //   updateBooleanResponse("google-rdp", state.googleRdp, questions);
  // }

  if (vendor && isGoogleAnalytics(vendor)) {
    updateBooleanResponse(
      "google-analytics-data-sharing",
      state.googleAnalytics && isUsRegion,
      questions,
    );
  }

  if (vendor && isShopify(vendor)) {
    updateBooleanResponse("shopify-sni", state.shopifySNI && isUsRegion, questions);
  }

  if (questions.length > 0) {
    await updateSurveyQuestions.mutateAsync(questions);
  }
};

const updateBooleanResponse = (slug: string, response: boolean, questions: any) => {
  questions.push({ slug: slug, answer: response ? "true" : "false" });
};

const updateVendorToSurveyResponse = (
  existing: string[],
  vendor: OrganizationDataRecipientDto,
  include: boolean,
  slug: string,
  questions: PatchOrganizationSurveyAnswerRequest[],
) => {
  const answerArrayIndex = existing.findIndex((x) => x === vendor.id);

  let updated = false;
  if (!include && answerArrayIndex != -1) {
    updated = true;
    existing.splice(answerArrayIndex, 1);
  }

  if (include && answerArrayIndex === -1) {
    updated = true;
    existing.push(vendor.id);
  }

  if (updated) {
    questions.push({ slug, answer: JSON.stringify(existing) });
  }
};

export const THIRD_PARTY_CLASSIFICATION_OPTION: VendorRadioOption<ClassificationType> = {
  label: "Third Party",
  value: "third-party",
  type: "file",
  key: "third-party",
};

export const CLASSIFICATION_OPTIONS: VendorRadioOption<ClassificationType>[] = [
  {
    label: "Service Provider",
    value: "service-provider",
    type: "file",
    key: "service-provider",
  },
  THIRD_PARTY_CLASSIFICATION_OPTION,
  {
    label: "Contractor",
    value: "contractor",
    type: "file",
    key: "contractor",
  },
];

export const useDataRecipientDisclosureSettings = (
  vendor: OrganizationDataRecipientDto | VendorDto,
  answers: Record<string, string> | null,
  org: OrganizationDto,
  custom: boolean,
  allVendors: OrganizationDataRecipientDto[],
  customCategory?: string,
) => {
  const [state, setState] = useState<DisclosureState>(initialStateFrom(null, null, org));

  const meta = useMemo(
    () => calculateDisclosureMeta(vendor, state, custom, answers, allVendors, customCategory),
    [vendor, state, custom, customCategory],
  );

  const updateState = useCallback((value: Partial<DisclosureState>) => {
    setState((prev) => ({ ...prev, ...value }));
  }, []);

  // calculate our initial state
  useEffect(() => {
    setState((prev) => (prev._isStateInitialized ? prev : initialStateFrom(vendor, answers, org)));
  }, [vendor, answers]);

  // auto-lock editing
  useEffect(() => {
    if (!state.ccpaIsSelling && state.isUploadVendor) {
      updateState({ isUploadVendor: false });
    }
  }, [state.ccpaIsSelling, state.isUploadVendor, updateState]);

  // auto-set classification
  useEffect(() => {
    if (state.classification != "third-party" && meta.autoSelectThirdPartyClassification) {
      updateState({
        classification: "third-party",
        publicTosUrl: null,
        serviceProviderFile: null,
        serviceProviderFilename: null,
      });
    }
  }, [state.classification, meta.autoSelectThirdPartyClassification, updateState]);

  return { state, meta, updateState };
};

export const DataRecipientDisclosureSettings: React.FC<VendorClassificationEditProps> = ({
  org,
  vendor,
  isThirdParty,
  disclosureState,
  disclosureMeta,
  updateDisclosureState,
}) => {
  const isUsRegion =
    disclosureState.processingRegions.includes("UNITED_STATES") || !org.featureGdpr;
  const isCanadaRegion = disclosureState.processingRegions.includes("CANADA");

  const {
    managedAdNetworks,
    addManagedRelationship,
    removeManagedRelationship,
    refresh: refreshManagedAdNetworks,
  } = useManagedAdNetworks(org.id);

  if (!disclosureMeta) {
    return null;
  }

  return (
    <div className="mt-xl">
      <header className="flex-grow">
        {(org.featureGdpr || org.featurePipeda) && (
          <>
            <div className="text-component-question"> Processing Regions </div>
            <Paras>
              <Para className="text-t2 text-component-help">
                Indicate whether {vendor.name} processes the data of consumers located in each
                region. For example, if your business’s shipping services in the US do not typically
                handle the data of European/Canadian customers, they would be US only.
              </Para>
            </Paras>
            <ProcessingRegionsSetting
              processingRegions={disclosureState.processingRegions}
              updateState={(processingRegions: ProcessingRegionEnum[]) =>
                updateDisclosureState({ processingRegions })
              }
            />
            <hr className="mt-xl mb-xl" />
          </>
        )}
      </header>

      {(isUsRegion || isCanadaRegion) && disclosureMeta.showClassification && (
        <section key={"classification"} className={clsx({ "mb-lg": org.featureGdpr })}>
          {org.featureGdpr && (
            <p className="text-h4">
              {isUsRegion && isCanadaRegion ? "US/Canada" : isCanadaRegion ? "Canada" : "US"} Data
              Disclosures
            </p>
          )}
          <div className={clsx({ "ml-lg": org.featureGdpr, "mb-lg": org.featureGdpr })}>
            {/* POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023) */}
            {/* <FacebookLduQuestion
                org={org}
                vendor={vendor}
                isThirdPartyRecipient={isThirdParty}
                state={state}
                update={updateState}
                meta={meta}
              />
              <GoogleRdpQuestion
                org={org}
                vendor={vendor}
                isThirdPartyRecipient={isThirdParty}
                state={state}
                update={updateState}
                meta={meta}
              /> */}

            <SharingQuestion
              org={org}
              vendor={vendor}
              isThirdPartyRecipient={isThirdParty}
              disclosureState={disclosureState}
              update={updateDisclosureState}
              disclosureMeta={disclosureMeta}
            />
            <CustomAudienceQuestion
              org={org}
              vendor={vendor}
              isThirdPartyRecipient={isThirdParty}
              disclosureState={disclosureState}
              update={updateDisclosureState}
              disclosureMeta={disclosureMeta}
              managedAdNetworks={managedAdNetworks}
              onManagerToggle={async (managerId: string, isChecked: boolean) => {
                if (isChecked) {
                  await addManagedRelationship(managerId, vendor.id);
                } else {
                  await removeManagedRelationship(managerId, vendor.id);
                }
                refreshManagedAdNetworks();
              }}
            />
            <GoogleAnalyticsQuestion
              org={org}
              vendor={vendor}
              isThirdPartyRecipient={isThirdParty}
              disclosureState={disclosureState}
              update={updateDisclosureState}
              disclosureMeta={disclosureMeta}
            />
            <ShopifySNIQuestion
              org={org}
              vendor={vendor}
              isThirdPartyRecipient={isThirdParty}
              disclosureState={disclosureState}
              update={updateDisclosureState}
              disclosureMeta={disclosureMeta}
            />
            <SellingQuestion
              org={org}
              vendor={vendor}
              isThirdPartyRecipient={isThirdParty}
              disclosureState={disclosureState}
              update={(newState: Partial<DisclosureState>) => {
                updateDisclosureState(newState);
              }}
              disclosureMeta={disclosureMeta}
            />
            <DirectSellingQuestion
              org={org}
              vendor={vendor}
              isThirdPartyRecipient={isThirdParty}
              disclosureState={disclosureState}
              update={updateDisclosureState}
              disclosureMeta={disclosureMeta}
            />
          </div>
        </section>
      )}
    </div>
  );
};

type QuestionProps = {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto | VendorDto;
  isThirdPartyRecipient: boolean;
  disclosureState: DisclosureState;
  disclosureMeta: DisclosureMeta;
  update: (update: Partial<DisclosureState>) => void;
  managedAdNetworks?: OrganizationManagedAdNetworkDto[];
  onManagerToggle?: (managerId: string, isChecked: boolean) => Promise<void>;
};

const SharingQuestion: React.FC<QuestionProps> = ({
  org,
  vendor,
  isThirdPartyRecipient,
  disclosureState,
  update,
  disclosureMeta,
}) => {
  if (!disclosureMeta.showSharing) {
    return null;
  }

  return (
    <VendorBooleanQuestion
      question={`${org.name} "shares" personal information with ${vendorName(
        vendor,
        isThirdPartyRecipient,
      )}`}
      condensed={false}
      helpText={
        <div className="text-t1">
          “Sharing” means providing personal information to ad networks for purposes of behavioral,
          or interest-based advertising.
        </div>
      }
      value={disclosureState.ccpaIsSharing}
      onChange={(newVal) => update({ ccpaIsSharing: newVal })}
    />
  );
};

const SellingQuestion: React.FC<QuestionProps> = ({
  org,
  vendor,
  isThirdPartyRecipient,
  disclosureState,
  update,
  disclosureMeta,
}) => {
  if (!disclosureMeta.showSelling) {
    return null;
  }
  const value = disclosureState.ccpaIsSelling || disclosureState.isAlwaysDataSelling;
  let tooltip = value
    ? "You have indicated you are using features which require this data recipient to be considered “selling” data."
    : "You have indicated you are using features which require this data recipient not to be considered “selling” data.";
  if (vendor.vendorKey == "shopify-audiences") {
    tooltip =
      "Shopify Audiences shares your customers' data with third parties for marketing purposes, which is considered “selling” in some regions.";
  }
  return (
    <VendorBooleanQuestion
      question={`${org.name} "sells" personal information to ${vendorName(
        vendor,
        isThirdPartyRecipient,
      )}`}
      disabled={disclosureMeta.lockCcpaIsSelling || disclosureState.isAlwaysDataSelling}
      disabledTooltip={tooltip}
      condensed={false}
      nextIndented={true}
      helpText={
        <div className="text-t1">
          "Selling" means providing personal data in exchange for money or anything else of value.
        </div>
      }
      value={value}
      onChange={(newVal) => update({ ccpaIsSelling: newVal })}
    />
  );
};

const DirectSellingQuestion: React.FC<QuestionProps> = ({
  org,
  vendor,
  isThirdPartyRecipient,
  disclosureState,
  update,
  disclosureMeta,
}) => {
  if (!disclosureMeta.showSelling) {
    return null;
  }
  return (
    <VendorBooleanQuestion
      indented={true}
      disabled={!disclosureState.ccpaIsSelling || disclosureState.isAlwaysDataSelling}
      disabledTooltip=""
      question={`${org.name} manually uploads, transmits, or gives ${vendorName(
        vendor,
        isThirdPartyRecipient,
      )} access to consumer data`}
      condensed={false}
      helpText={
        <div className="text-t1">
          Answer "no" if they receive data only through automated means on your website, such as a
          cookie.
        </div>
      }
      value={disclosureState.isUploadVendor || disclosureState.isAlwaysUploadVendor}
      onChange={(newVal) => update({ isUploadVendor: newVal })}
    />
  );
};

// Do we want these questions to be defined separate from Get Compliant?
const CustomAudienceQuestion: React.FC<QuestionProps> = ({
  org,
  vendor,
  isThirdPartyRecipient,
  disclosureState,
  update,
  disclosureMeta,
  managedAdNetworks = [],
  onManagerToggle,
}) => {
  if (!disclosureMeta.showCustomAudience) {
    return null;
  }

  // Get current managers for this network from the database
  const currentManagers = managedAdNetworks.filter((item) => item.networkId === vendor.id);
  const currentManagerIds = new Set(currentManagers.map((item) => item.managerId));

  return (
    <>
      <VendorBooleanQuestion
        indented={false}
        disabled={!disclosureMeta.showCustomAudience}
        question={`${org.name} uses the "Custom Audience" feature for ${vendorName(
          vendor,
          isThirdPartyRecipient,
        )}`}
        condensed={false}
        helpText={
          <div className="text-t1">
            <ExternalLink
              className="bold-link"
              href="https://www.facebook.com/business/help/170456843145568?id=2469097953376494"
            >
              Custom Audience
            </ExternalLink>{" "}
            is a service offered by most ad networks in which a business can upload a list of their
            customers or contacts, and the ad network will either deliver ads directly to those
            people and/or use it to create a "look-alike" audience.
          </div>
        }
        value={disclosureState.usesCustomAudience}
        onChange={(newVal) => update({ usesCustomAudience: newVal })}
      />
      {disclosureState.usesCustomAudience && disclosureMeta.potentialCustomAudienceManagers && (
        <>
          {disclosureMeta.potentialCustomAudienceManagers.map((manager) => (
            <VendorBooleanQuestion
              key={manager.id}
              indented={true}
              question={`Audience lists in ${vendorName(
                vendor,
                isThirdPartyRecipient,
              )} are managed via ${manager.name}`}
              condensed={false}
              helpText={undefined}
              value={currentManagerIds.has(manager.id)}
              onChange={async (newVal) => {
                if (onManagerToggle) {
                  await onManagerToggle(manager.id, newVal);
                }
              }}
            />
          ))}
        </>
      )}
    </>
  );
};

// POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
// const FacebookLduQuestion: React.FC<QuestionProps> = ({
//   org,
//   vendor,
//   isThirdPartyRecipient,
//   state,
//   update,
//   meta,
// }) => {
//   if (!meta.showFacebookLdu) {
//     return null;
//   }

//   return (
//     <VendorBooleanQuestion
//       question={`${org.name} will enable LDU for ${vendorName(vendor, isThirdPartyRecipient)}`}
//       condensed={false}
//       helpText={<FacebookLduHelp />}
//       value={state.facebookLdu}
//       onChange={(newVal) => update({ facebookLdu: newVal })}
//     />
//   );
// };

export const FacebookLduHelp = () => {
  return (
    <div className="text-t1">
      Enabling “Limited Data Use” (LDU) in your Facebook Ads configuration will "turn off"
      personalized advertising for California consumers. If personalized advertising is disabled for
      all of your ad networks, your business will not be required to post the "Do Not Share My
      Personal Information" link.{" "}
      <a
        href="https://www.facebook.com/business/help/1151133471911882"
        target="_blank"
        rel="noopener noreferrer"
        className="text-medium text-link"
      >
        How to enable LDU
      </a>
    </div>
  );
};

// POLARIS-2512 - We are removing the LDU and RDP questions for now. Revisit this when Facebook updates Policy or in 3 Months (June 2023)
// const GoogleRdpQuestion: React.FC<QuestionProps> = ({
//   org,
//   vendor,
//   isThirdPartyRecipient,
//   state,
//   update,
//   meta,
// }) => {
//   if (!meta.showGoogleRdp) {
//     return null;
//   }
//   return (
//     <VendorBooleanQuestion
//       question={`${org.name} will enable RDP for ${vendorName(vendor, isThirdPartyRecipient)}`}
//       condensed={false}
//       helpText={<GoogleRdpHelp />}
//       value={state.googleRdp}
//       onChange={(newVal) => update({ googleRdp: newVal })}
//     />
//   );
// };

export const GoogleRdpHelp = () => {
  return (
    <div className="text-t1">
      Enabling “Restricted Data Processing” (RDP) in your Google Ads configuration will "turn off"
      personalized advertising for California consumers. If personalized advertising is disabled for
      all of your ad networks, your business will not be required to post the "Do Not Share My
      Personal Information" link. Follow the{" "}
      <a
        href={`https://support.google.com/google-ads/answer/9606827#zippy=%2Cedit-your-global-site-tag-to-disable-ad-personalization-signals`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-medium text-link"
      >
        instructions to enable RDP
      </a>{" "}
      and be sure to update your global site tag.
    </div>
  );
};

const GoogleAnalyticsQuestion: React.FC<QuestionProps> = ({
  org,
  vendor,
  isThirdPartyRecipient,
  disclosureState,
  update,
  disclosureMeta,
}) => {
  if (!disclosureMeta.showGoogleAnalytics) {
    return null;
  }

  return (
    <VendorBooleanQuestion
      question={`Has ${org.name} turned off (or will it turn off) data sharing in ${vendorName(
        vendor,
        isThirdPartyRecipient,
      )}?`}
      condensed={false}
      helpText={<GoogleAnalyticsHelp />}
      value={disclosureState.googleAnalytics}
      onChange={(newVal) => update({ googleAnalytics: newVal, ccpaIsSelling: !newVal })}
    />
  );
};

export const GoogleAnalyticsHelp = () => {
  return (
    <div className="text-t1">
      By default, Google accesses consumer data (for example, data collected from website visitors)
      to enhance its services. If your business turns off data sharing, Google will no longer access
      that data. If data sharing is on, you will be required to post a "Do Not Sell My Personal
      Information" link on your website.{" "}
      <a
        href={`https://support.google.com/analytics/answer/1011397?hl=en&ref_topic=2919631`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-medium text-link"
      >
        How to turn off data sharing in Google Analytics
      </a>
    </div>
  );
};

const ShopifySNIQuestion = ({ org, disclosureState, update, disclosureMeta }: QuestionProps) => {
  if (!disclosureMeta.showShopifySNI) {
    return null;
  }

  return (
    <VendorBooleanQuestion
      question={`Has ${org.name} turned off Shopify Network Intelligence (SNI)?`}
      condensed={false}
      helpText={<ShopifySNIHelp />}
      value={disclosureState.shopifySNI}
      onChange={(newVal) => update({ shopifySNI: newVal })}
    />
  );
};

export const ShopifySNIHelp = () => {
  return (
    <div className="text-t1">
      By default, Shopify automatically opts stores into SNI, which gives them access to Shopify
      Audiences and other features. You can{" "}
      <a
        href="https://help.shopify.com/en/manual/privacy-and-security/privacy/customer-privacy-settings/privacy-settings#network-intelligence"
        target="_blank"
        rel="noopener noreferrer"
        className="text-medium text-link"
      >
        read about SNI here
      </a>
      . When SNI is enabled, your business is "selling" and "sharing" data with Shopify, which
      triggers opt-out requirements. If you answer "no" (meaning you left the default settings on),
      we'll automatically incorporate those requirements into your Data Map and Privacy Center.
    </div>
  );
};

type ServiceProviderLanguageProps = {
  isThirdPartyRecipient: boolean;
  disclosureMeta: DisclosureMeta;
  vendor: OrganizationDataRecipientDto | VendorDto;
};

export const ServiceProviderLanguage: React.FC<ServiceProviderLanguageProps> = ({
  isThirdPartyRecipient,
  vendor,
  disclosureMeta,
}) => {
  let content = null;

  if (isThirdPartyRecipient) {
    return null;
  }

  if (disclosureMeta.lockClassification) {
    content = <LockedThirdPartyServiceProviderLanguage />;
  } else if (vendor.serviceProviderLanguageUrl) {
    content = (
      <ExistingServiceProviderLanguage
        vendor={vendor}
        isThirdPartyRecipient={isThirdPartyRecipient}
      />
    );
  } else {
    content = <>Are they considered a service provider or a third party under U.S. privacy laws.</>;
  }

  return (
    <>
      <p className="text-neutral-500 leading-[16px] text-[14px] font-normal">{content}</p>
      <ServiceProviderDocumentationLink
        vendor={vendor}
        isThirdPartyRecipient={isThirdPartyRecipient}
      />
    </>
  );
};

type ExistingServiceProviderLanguageProps = {
  vendor: OrganizationDataRecipientDto | VendorDto;
  isThirdPartyRecipient: boolean;
};

const ExistingServiceProviderLanguage: React.FC<ExistingServiceProviderLanguageProps> = ({
  vendor,
  isThirdPartyRecipient,
}) => {
  const statusLink = (
    <a
      className={"text-primary-600 underline font-medium"}
      href="https://knowledge.truevault.com/article/294-identifying-service-provider-language"
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn more about determining service provider status
    </a>
  );

  if (vendor.serviceProviderRecommendation == "SERVICE_PROVIDER") {
    return (
      <>
        We have found documentation to support classifying this vendor as a service provider to your
        business. Review the documentation and change the selection if needed. {statusLink}
      </>
    );
  }

  return (
    <>
      We have reviewed {vendorName(vendor, isThirdPartyRecipient)}'s terms of service and have not
      found service provider language. Review the documentation and change the selection if needed.
      If you can find the service provider language, mark the vendor as a Service Provider.
      Otherwise, leave them as a Third Party. {statusLink}
    </>
  );
};

const LockedThirdPartyServiceProviderLanguage = () => {
  const statusLink = (
    <a
      className={"text-primary-600 underline font-medium"}
      href="https://knowledge.truevault.com/article/294-identifying-service-provider-language"
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn more about determining service provider status
    </a>
  );

  return (
    <>
      Based on your Vendor Settings, this recipient is a Third party to your business. {statusLink}
    </>
  );
};

const ServiceProviderDocumentationLink = ({
  vendor,
  isThirdPartyRecipient,
}: {
  vendor: OrganizationDataRecipientDto | VendorDto;
  isThirdPartyRecipient: boolean;
}) => {
  if (!vendor.serviceProviderLanguageUrl) {
    return null;
  }

  if (
    vendor.serviceProviderRecommendation != "SERVICE_PROVIDER" &&
    vendor.serviceProviderRecommendation != "THIRD_PARTY"
  ) {
    return null;
  }

  const label =
    vendor.serviceProviderRecommendation == "SERVICE_PROVIDER"
      ? `${vendorName(vendor, isThirdPartyRecipient)} service provider documentation`
      : `${vendorName(vendor, isThirdPartyRecipient)} terms of service`;

  return (
    <div className="mb-md">
      <a
        className={"text-primary-600 underline font-medium"}
        target="_blank"
        rel="noopener noreferrer"
        href={vendor.serviceProviderLanguageUrl}
      >
        {label}
      </a>
    </div>
  );
};

export function GdprProcessorLanguage({
  vendor,
  meta,
}: {
  meta: DisclosureMeta;
  vendor: OrganizationDataRecipientDto | VendorDto;
}) {
  let content = null;

  if (meta.recommendClassificationProcessor) {
    content = (
      <>
        <p className="leading-[16px]">
          To be considered a "Processor" under GDPR, a vendor must have sufficient guarantees in
          place about the security of its data processing and must adhere to an approved code of
          conduct. Otherwise, the vendor is a “Controller” of personal data it handles in relation
          to your business.
        </p>
        <p>We've automatically designated {vendor.name} as "Processor" based on our research.</p>
      </>
    );
  } else if (meta.recommendClassificationController) {
    content = (
      <>
        <p className="leading-[16px]">
          We've automatically designated {vendor.name} as "Controller" based on our research.{" "}
          <span className="font-medium">
            Note that your Data Recipients who are Controllers will be disclosed by name in your
            Privacy Policy.
          </span>
        </p>
      </>
    );
  } else {
    content = (
      <>
        <p>Are they considered a processor or controller under GDPR.</p>
      </>
    );
  }

  return <div className="text-neutral-500 leading-[16px] text-[14px] font-normal">{content}</div>;
}
